/**
 * Post–add-to-cart drawer sync: schedules authoritative section HTML from /cart/add.js
 * without the legacy fixed delay. Exposes optional performance marks when ?cart_perf=1
 * or window.__ISALBI_CART_PERF__ is set.
 *
 * Optional console timing logs (trigger → rAF → render done):
 *   ?cart_perf=1 | ?cart_sync_log=1 | window.__ISALBI_CART_SYNC_LOG__ = true
 */
(function cartAddSyncIife() {
  'use strict';

  if (window.__isalbiCartAddSync) {
    return;
  }

  let actionSeq = 0;

  function isPerfEnabled() {
    try {
      return (
        window.__ISALBI_CART_PERF__ === true ||
        (typeof window.location !== 'undefined' && /[?&]cart_perf=1(?:&|$)/.test(window.location.search))
      );
    } catch (e) {
      return false;
    }
  }

  /** Logging mirrors perf flags plus cart_sync_log=1 for console-only diagnostics. */
  function isLogEnabled() {
    try {
      if (isPerfEnabled()) {
        return true;
      }
      if (window.__ISALBI_CART_SYNC_LOG__ === true) {
        return true;
      }
      if (typeof window.location !== 'undefined' && /[?&]cart_sync_log=1(?:&|$)/.test(window.location.search)) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * @param {string} phase
   * @param {Record<string, unknown>} detail
   */
  function logTiming(phase, detail) {
    if (!isLogEnabled()) {
      return;
    }
    const payload = Object.assign({ phase }, detail);
    // eslint-disable-next-line no-console
    console.info('[IsAlbi CartSync]', JSON.stringify(payload));
  }

  function mark(name) {
    if (!isPerfEnabled() || !window.performance || typeof performance.mark !== 'function') {
      return;
    }
    try {
      performance.mark(`isalbi:${name}`);
    } catch (e) {
      // ignore
    }
  }

  /**
   * @param {HTMLElement | null} cartDrawer
   * @param {boolean} pending
   */
  function setDrawerPending(cartDrawer, pending) {
    if (!cartDrawer || typeof cartDrawer.setAttribute !== 'function') {
      return;
    }
    if (pending) {
      cartDrawer.setAttribute('data-cart-sync', 'pending');
      cartDrawer.setAttribute('aria-busy', 'true');
    } else {
      cartDrawer.removeAttribute('data-cart-sync');
      cartDrawer.removeAttribute('aria-busy');
    }
  }

  /**
   * After successful /cart/add.js, apply section HTML with minimal deferral so the header
   * cart icon can paint first. Latest scheduled run wins (rapid sequential adds).
   * @param {object} opts
   * @param {HTMLElement | null} [opts.cartDrawer] - `cart-drawer` custom element
   * @param {object} opts.data - Add response including `sections`
   * @param {boolean} opts.openDrawerAfter
   * @param {() => void|Promise<void>} [opts.onCartPageRefresh] - When no cart drawer (e.g. /cart page)
   */
  function schedulePostAddDrawerUpdate(opts) {
    const { cartDrawer, data, openDrawerAfter, onCartPageRefresh } = opts;
    const id = ++actionSeq;
    const tSchedule = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

    mark('cart-add:before-drawer-schedule');

    logTiming('schedule', {
      actionId: id,
      tScheduleMs: Math.round(tSchedule * 100) / 100,
      path: cartDrawer ? 'drawer' : typeof onCartPageRefresh === 'function' ? 'cart-page-refresh' : 'none',
      openDrawerAfter: Boolean(openDrawerAfter)
    });

    const runUpdate = async () => {
      if (id !== actionSeq) {
        logTiming('skipped-stale', { actionId: id, currentActionId: actionSeq });
        return;
      }
      const tAfterRaf = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      logTiming('after-raf', {
        actionId: id,
        msSinceSchedule: Math.round((tAfterRaf - tSchedule) * 100) / 100
      });

      setDrawerPending(cartDrawer, true);
      mark('cart-add:before-render');
      const tRenderStart = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      logTiming('render-start', {
        actionId: id,
        msSinceSchedule: Math.round((tRenderStart - tSchedule) * 100) / 100
      });

      try {
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          await cartDrawer.renderContents(data, openDrawerAfter);
        } else if (typeof onCartPageRefresh === 'function') {
          await onCartPageRefresh();
        }
      } catch (error) {
        const tErr = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        logTiming('render-error', {
          actionId: id,
          msSinceSchedule: Math.round((tErr - tSchedule) * 100) / 100,
          message: error && error.message ? String(error.message) : 'unknown'
        });
        console.error('[CartAddSync] Drawer update failed:', error); // eslint-disable-line
        throw error;
      } finally {
        if (id === actionSeq) {
          mark('cart-add:after-render');
          const tDone = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
          logTiming('render-complete', {
            actionId: id,
            msSinceSchedule: Math.round((tDone - tSchedule) * 100) / 100,
            msRender: Math.round((tDone - tRenderStart) * 100) / 100
          });
          setDrawerPending(cartDrawer, false);
        }
      }
    };

    // Single rAF: icon bubble is updated synchronously before this runs; one frame is enough to paint it.
    requestAnimationFrame(() => {
      runUpdate().catch(() => {
        if (id === actionSeq) {
          setDrawerPending(cartDrawer, false);
        }
      });
    });
  }

  const api = {
    /** @returns {number} monotonic id for the latest scheduled post-add update */
    getLatestActionSeq: () => actionSeq,
    /** @param {number} id */
    isCurrentAction: (id) => id === actionSeq,
    schedulePostAddDrawerUpdate,
    setDrawerPending,
    mark,
    isPerfEnabled,
    isLogEnabled
  };

  if (typeof window !== 'undefined' && window.__ISALBI_TEST__) {
    api._resetForTest = () => {
      actionSeq = 0;
    };
  }

  window.__isalbiCartAddSync = api;
})();
